import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgress, IconButton, Skeleton } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import {
  WorkerDetailsCard,
  OnboardingStepsCard,
  CheckHQCard,
  ZealCard,
  CheckrCard,
  WorkerSkillsCard,
} from './components';

import Avatar from '@/components/Avatar';
import Badge from '@/components/Badge';
import Page from '@/components/Page';

import Stack from '@/components/Stack';
import { Body, Small, Title } from '@/components/Typography';
import {
  WorkersV2API,
  WorkerDetail,
  ExternalsResponse,
} from '@/services/workersV2API';

const STACK_GAP = 24;

const PageSkeleton = () => (
  <Page
    media={<Skeleton height={80} variant="circular" width={80} />}
    title={
      <Stack vertical gap={8}>
        <Skeleton height={32} width={200} />
        <Skeleton height={20} width={150} />
      </Stack>
    }
  >
    <div
      style={{
        display: 'grid',
        gap: '24px',
        gridTemplateAreas: '"primary secondary"',
        gridTemplateRows: 'auto',
        gridTemplateColumns: '1fr 320px',
      }}
    >
      <div style={{ gridArea: 'primary' }}>
        <Stack vertical gap={STACK_GAP}>
          <Skeleton height={300} variant="rounded" />
          <Skeleton height={400} variant="rounded" />
        </Stack>
      </div>
      <div style={{ gridArea: 'secondary' }}>
        <Stack vertical gap={STACK_GAP}>
          <Skeleton height={500} variant="rounded" />
          <Skeleton height={300} variant="rounded" />
        </Stack>
      </div>
    </div>
  </Page>
);

const WorkerV2 = () => {
  const { workerId } = useParams<{ workerId: string }>();
  const navigate = useNavigate();

  const [worker, setWorker] = useState<WorkerDetail | null>(null);
  const [externals, setExternals] = useState<ExternalsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchWorkerData = useCallback(async () => {
    if (!workerId) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch both worker details and externals in parallel
      const [workerResponse, externalsResponse] = await Promise.all([
        WorkersV2API.getWorker(parseInt(workerId)),
        WorkersV2API.getWorkerExternals(parseInt(workerId)),
      ]);

      if (workerResponse.success) {
        setWorker(workerResponse.data);
      } else {
        setError('Failed to fetch worker details');
      }

      if (externalsResponse.success) {
        setExternals(externalsResponse);
      }
    } catch (err) {
      console.error('Error fetching worker data:', err);
      setError('Failed to fetch worker data');
    } finally {
      setLoading(false);
    }
  }, [workerId]);

  useEffect(() => {
    fetchWorkerData();
  }, [fetchWorkerData]);

  const handleBack = () => {
    navigate('/workers_v2');
  };

  const handleSyncComplete = () => {
    fetchWorkerData();
  };

  if (loading) {
    return <PageSkeleton />;
  }

  if (error || !worker) {
    return (
      <Page title="Worker Not Found">
        <Stack
          vertical
          gap={16}
          style={{ padding: '40px', alignItems: 'center' }}
        >
          <Body color="lighter">{error || 'Worker not found'}</Body>
          <IconButton onClick={handleBack}>
            <ArrowBack /> Back to Workers
          </IconButton>
        </Stack>
      </Page>
    );
  }

  return (
    <Page
      size="xl"
      headerMenu={
        <IconButton onClick={handleBack} size="small">
          <ArrowBack />
        </IconButton>
      }
      media={
        <Avatar
          firstName={worker.user?.first_name || ''}
          size="lg"
          src={worker.avatar_url || undefined}
        />
      }
      title={
        <Stack vertical gap={4}>
          <Stack gap={12} style={{ alignItems: 'center' }}>
            <Title>{worker.user?.full_name || 'N/A'}</Title>
            <Badge
              label={worker.active ? 'Enabled' : 'Disabled'}
              status={worker.active ? 'theme' : 'danger'}
            />
            <Badge
              label={worker.tax_type?.toUpperCase() || 'N/A'}
              status={worker.tax_type === 'w2' ? 'theme' : 'neutral'}
            />
          </Stack>
          <Stack gap={16}>
            <Small color="lighter">{worker.user?.email || 'N/A'}</Small>
            {worker.user?.phone_number && (
              <Small color="lighter">{worker.user.phone_number}</Small>
            )}
          </Stack>
          <Stack gap={16} style={{ marginTop: '8px' }}>
            <Stack gap={4}>
              <Small color="lighter">Jobs Worked:</Small>
              <Body weight="semibold">
                {worker.stats?.jobs_worked ?? 'N/A'}
              </Body>
            </Stack>
            <Stack gap={4}>
              <Small color="lighter">Jobs Dropped:</Small>
              <Body weight="semibold">
                {worker.stats?.jobs_dropped ?? 'N/A'}
              </Body>
            </Stack>
            <Stack gap={4}>
              <Small color="lighter">Rating:</Small>
              <Body weight="semibold">
                {worker.ratings?.average_rating
                  ? `${worker.ratings.average_rating.toFixed(1)} (${worker.ratings.jobs_rating_count ?? 0})`
                  : 'N/A'}
              </Body>
            </Stack>
          </Stack>
        </Stack>
      }
    >
      <div
        style={{
          display: 'grid',
          gap: '24px',
          gridTemplateAreas: '"primary secondary"',
          gridTemplateRows: 'auto',
          gridTemplateColumns: '1fr 320px',
        }}
      >
        <div style={{ gridArea: 'primary', minWidth: 0 }}>
          <Stack vertical gap={STACK_GAP}>
            <WorkerSkillsCard workerId={worker.id} />
            <OnboardingStepsCard workerId={worker.id} />
            {externals && (
              <>
                <CheckHQCard
                  checkhq={externals.checkhq}
                  workerId={worker.id}
                  onSync={handleSyncComplete}
                />
                <ZealCard
                  zeal={externals.zeal}
                  workerId={worker.id}
                  onSync={handleSyncComplete}
                />
                <CheckrCard
                  checkr={externals.checkr}
                  workerId={worker.id}
                  onSync={handleSyncComplete}
                />
              </>
            )}
          </Stack>
        </div>
        <div style={{ gridArea: 'secondary' }}>
          <Stack vertical gap={STACK_GAP}>
            <WorkerDetailsCard
              worker={worker}
              checkrCandidateId={externals?.checkr?.candidate_id}
              zealEmployeeId={externals?.zeal?.employee_id}
            />
          </Stack>
        </div>
      </div>
    </Page>
  );
};

export default WorkerV2;
